import { useState } from 'react';
import { Link } from 'react-router-dom';
import { NotebookPen, RotateCcw, Trash2 } from 'lucide-react';
import { isDue, type WordRecordView, type WordStatus } from '@en3/shared';
import { ROUTES } from '@/app/routes';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useNow } from '@/lib/useNow';
import { WORD_STATUS_LABELS } from './WordbookFilters';

/**
 * 生词本的一行。
 *
 * 信息取舍：这一行的目标是"扫一眼就能决定下一步做什么"，所以只放
 * 词形 / 音标 / 释义 / 等级 / 状态 / 熟悉度 / 查词次数 / 到期时间 / 首次遇见，
 * 原句只留两行（`line-clamp-2`）—— 完整语境属于复习卡与阅读页，
 * 列表里铺满整句会把"找那个词"的扫描效率毁掉。
 *
 * 删除采用**行内二次确认**而不是全局弹窗：删除是不可逆的，确认必须贴着被删对象，
 * 否则用户很容易在"以为在删 A"的情况下删掉 B。确认文案带词形，进一步绑定对象。
 *
 * `firstArticleId` 存在时才给链接：历史数据里可能有"文章已删、记录还在"的词，
 * 这时显示原文标题会是一个点进去 404 的死链。
 */

/** 状态 → 语义色。故意不用红色标"新词"：新词不是错误，只是还没学。 */
const STATUS_TONES: Record<WordStatus, 'primary' | 'warning' | 'danger' | 'success' | 'muted'> = {
  new: 'primary',
  fuzzy: 'warning',
  unknown: 'danger',
  known: 'success',
  mastered: 'success',
};

/**
 * 到期时间的相对说法。
 *
 * 注意 `isDue` 带 15 分钟容差（见 shared/lib/srs）：容差内其实是"马上要到期"而不是"已过期"，
 * 所以这里分开说，避免刚点完"忘记"就看到"已到期"以为数据错了。
 */
function formatDue(dueAt: number | null, now: number): string {
  if (dueAt === null) return '未排期';
  if (isDue(dueAt, now)) return dueAt <= now ? '已到期' : '即将到期';

  const diff = dueAt - now;
  const minutes = Math.round(diff / 60_000);
  if (minutes < 60) return `${minutes} 分钟后`;
  const hours = Math.round(diff / 3_600_000);
  if (hours < 24) return `${hours} 小时后`;
  const days = Math.round(diff / 86_400_000);
  if (days < 30) return `${days} 天后`;
  return new Date(dueAt).toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' });
}

export function WordRecordRow({
  record,
  onEdit,
  onDelete,
  deleting = false,
}: {
  record: WordRecordView;
  onEdit: (record: WordRecordView) => void;
  onDelete: (record: WordRecordView) => void;
  /** 该行正在被删除（只对当前行显示 loading，避免整列表按钮一起转圈） */
  deleting?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  // 「现在的时刻」来自共享时钟（见 lib/useNow）：渲染期间调 Date.now() 是不纯的，
  // 而且相对时间只需要分钟级精度，一分钟刷新一次足够，也不会让整页跟着 tick 重渲染。
  const now = useNow();
  const phonetic = record.phoneticUk ?? record.phoneticUs;
  const familiarity = Math.max(0, Math.min(100, Math.round(record.familiarity)));
  const due = formatDue(record.dueAt, now);

  return (
    <li className="rounded-lg border bg-card p-3 text-card-foreground shadow-sm">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span className="text-base font-semibold break-all">{record.surface}</span>
            {phonetic && <span className="font-mono text-xs text-muted-foreground">{phonetic}</span>}
            {record.pos && <Badge tone="muted">{record.pos}</Badge>}
            {record.cefrLevel && (
              <Badge tone="primary" title="CEFR 难度等级">
                {record.cefrLevel}
              </Badge>
            )}
            <Badge tone={STATUS_TONES[record.status]} title="生词本状态">
              {WORD_STATUS_LABELS[record.status]}
            </Badge>
          </div>

          <p className="mt-1 text-sm">{record.translation ?? '暂无释义'}</p>

          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <span className="flex items-center gap-1.5">
              <span>熟悉度</span>
              <span
                role="progressbar"
                aria-label={`熟悉度 ${familiarity}%`}
                aria-valuenow={familiarity}
                aria-valuemin={0}
                aria-valuemax={100}
                className="h-1.5 w-20 overflow-hidden rounded-full bg-muted"
              >
                <span className="block h-full rounded-full bg-primary" style={{ width: `${familiarity}%` }} />
              </span>
              <span className="tabular-nums">{familiarity}</span>
            </span>
            <span>查词 {record.lookupCount} 次</span>
            <span>{due}</span>
            {record.tags.length > 0 && (
              <span className="flex flex-wrap items-center gap-1">
                {record.tags.slice(0, 4).map((tag) => (
                  <Badge key={tag} tone="muted">
                    {tag}
                  </Badge>
                ))}
                {record.tags.length > 4 && <span>+{record.tags.length - 4}</span>}
              </span>
            )}
          </div>

          {(record.firstArticleId ?? record.firstContext) && (
            <p className="mt-2 text-xs text-muted-foreground">
              {record.firstArticleId && (
                <>
                  首次遇见：
                  <Link
                    to={ROUTES.reader(record.firstArticleId)}
                    className="text-primary underline underline-offset-4"
                  >
                    {record.firstArticleTitle ?? '查看原文'}
                  </Link>
                </>
              )}
              {record.firstContext && (
                <span className={`block font-serif text-foreground/80 ${record.firstArticleId ? 'mt-1' : ''} line-clamp-2`}>
                  {record.firstContext}
                </span>
              )}
            </p>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1.5">
          <Button
            size="sm"
            variant="outline"
            className="px-2"
            onClick={() => onEdit(record)}
            title="编辑笔记与标签"
            aria-label={`编辑「${record.surface}」的笔记与标签`}
          >
            <NotebookPen aria-hidden className="size-3.5" />
          </Button>
          <Link
            to={ROUTES.review}
            title="立即复习这张"
            aria-label={`立即复习「${record.surface}」`}
            className="inline-flex h-8 items-center gap-2 rounded-md border bg-background px-2 text-xs hover:bg-accent hover:text-accent-foreground"
          >
            <RotateCcw aria-hidden className="size-3.5" />
          </Link>

          {confirming ? (
            <div className="flex flex-col items-end gap-1" role="status">
              <span className="text-[11px] text-muted-foreground">删除「{record.surface}」？</span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="destructive"
                  className="px-2"
                  loading={deleting}
                  onClick={() => onDelete(record)}
                  aria-label={`确认删除「${record.surface}」`}
                >
                  删除
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="px-2"
                  onClick={() => setConfirming(false)}
                  aria-label="取消删除"
                >
                  取消
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="ghost"
              className="px-2"
              onClick={() => setConfirming(true)}
              title="从生词本删除"
              aria-label={`删除「${record.surface}」`}
            >
              <Trash2 aria-hidden className="size-3.5" />
            </Button>
          )}
        </div>
      </div>
    </li>
  );
}
